import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import EventSectionTabs, { EVENT_TAB_MANAGEMENT } from "../components/EventSectionTabs";
import SectionTabsPageHeader from "../components/SectionTabsPageHeader";
import Events from "./Events";
import EventParticipants from "./EventParticipants";
import EventParticipantDetails from "./EventParticipantDetails";
import CloseEventRecord from "./CloseEventRecord";
import {
  EVENT_MGMT_TAB_CLOSE_RECORD,
  EVENT_MGMT_TAB_EVENTS,
  EVENT_MGMT_TAB_PARTICIPANTS,
  EVENT_MGMT_TAB_PARTICIPANTS_DETAILS,
  getDefaultEventManagementTab,
  getPermittedEventManagementTabs,
} from "../shared/utils/eventManagementTabs";

function EventManagement() {
  const { hasPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");

  const allowedTabs = useMemo(
    () => getPermittedEventManagementTabs(hasPermission),
    [hasPermission]
  );

  const defaultTab = useMemo(
    () => getDefaultEventManagementTab(hasPermission),
    [hasPermission]
  );

  const activeTab =
    tabFromUrl && allowedTabs.includes(tabFromUrl) ? tabFromUrl : defaultTab;

  const showEventsTab = allowedTabs.includes(EVENT_MGMT_TAB_EVENTS);
  const showParticipantsTab = allowedTabs.includes(EVENT_MGMT_TAB_PARTICIPANTS);
  const showParticipantsDetailsTab = allowedTabs.includes(EVENT_MGMT_TAB_PARTICIPANTS_DETAILS);
  const showCloseTab = allowedTabs.includes(EVENT_MGMT_TAB_CLOSE_RECORD);

  useEffect(() => {
    if (!tabFromUrl || !allowedTabs.includes(tabFromUrl)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", activeTab);
          return next;
        },
        { replace: true }
      );
    }
  }, [tabFromUrl, activeTab, allowedTabs, setSearchParams]);

  return (
    <div className="page-container page-container-full page-container--section-tabs">
      <SectionTabsPageHeader subtitle="Manage events, participants, and event close records in one place.">
        <EventSectionTabs activeTab={EVENT_TAB_MANAGEMENT} />
      </SectionTabsPageHeader>
      <div className="tabs-bar">
        <div className="tabs-list">
          {showEventsTab && (
            <button
              type="button"
              className={`tab-btn ${activeTab === EVENT_MGMT_TAB_EVENTS ? "active" : ""}`}
              onClick={() => setSearchParams({ tab: EVENT_MGMT_TAB_EVENTS })}
            >
              Events
            </button>
          )}
          {showParticipantsTab && (
            <button
              type="button"
              className={`tab-btn ${activeTab === EVENT_MGMT_TAB_PARTICIPANTS ? "active" : ""}`}
              onClick={() => setSearchParams({ tab: EVENT_MGMT_TAB_PARTICIPANTS })}
            >
              Event Sign-ups
            </button>
          )}
          {showParticipantsDetailsTab && (
            <button
              type="button"
              className={`tab-btn ${activeTab === EVENT_MGMT_TAB_PARTICIPANTS_DETAILS ? "active" : ""}`}
              onClick={() => setSearchParams({ tab: EVENT_MGMT_TAB_PARTICIPANTS_DETAILS })}
            >
              Participant Profiles
            </button>
          )}
          {showCloseTab && (
            <button
              type="button"
              className={`tab-btn ${activeTab === EVENT_MGMT_TAB_CLOSE_RECORD ? "active" : ""}`}
              onClick={() => setSearchParams({ tab: EVENT_MGMT_TAB_CLOSE_RECORD })}
            >
              Record & Close
            </button>
          )}
        </div>
      </div>
      <div className="tab-content">
        {activeTab === EVENT_MGMT_TAB_EVENTS && showEventsTab && <Events />}
        {activeTab === EVENT_MGMT_TAB_PARTICIPANTS && showParticipantsTab && <EventParticipants />}
        {activeTab === EVENT_MGMT_TAB_PARTICIPANTS_DETAILS && showParticipantsDetailsTab && (
          <EventParticipantDetails />
        )}
        {activeTab === EVENT_MGMT_TAB_CLOSE_RECORD && showCloseTab && <CloseEventRecord />}
      </div>
    </div>
  );
}

export default EventManagement;
